import './dateInput.scss';

import { DatePicker, type DatePickerProps } from 'antd';
import { observer } from 'mobx-react-lite';
import { Dayjs } from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';

import { useOutsideMouseDown } from '../../hooks';
import { CalendarSVGIcon } from '../../svg';
import { RefObject } from 'react';
import { DateInputState } from './dateInputState';
import { DateInputFooter } from './dateInputFooter';
import { PickerLocale } from 'antd/lib/date-picker/generatePicker';
import { Feedback } from '..';

export type PanelMode = NonNullable<DatePickerProps['mode']>;

const { RangePicker } = DatePicker;
export const locale: PickerLocale = {
  "lang": {
    "locale": "en_US",
    "placeholder": "",
    "rangePlaceholder": ["", ""],
    "today": "Today",
    "week": "Week",
    "now": "Now",
    "backToToday": "",
    "ok": "",
    "clear": "",
    "month": "",
    "year": "",
    "timeSelect": "Select time",
    "dateSelect": "Select date",
    "monthSelect": "Choose a month",
    "yearSelect": "Choose a year",
    "decadeSelect": "Choose a decade",
    "yearFormat": "YYYY",
    "dateFormat": "DD/MM/YYYY",
    "dayFormat": "D",
    "dateTimeFormat": "M/D/YYYY HH:mm:ss",
    "monthFormat": "MMMM",
    "monthBeforeYear": true,
    "previousMonth": "Previous month (PageUp)",
    "nextMonth": "Next month (PageDown)",
    "previousYear": "Last year (Control + left)",
    "nextYear": "Next year (Control + right)",
    "previousDecade": "Last decade",
    "nextDecade": "Next decade",
    "previousCentury": "Last century",
    "nextCentury": "Next century"
  },
  "timePickerLocale": {
    "placeholder": "Select time"
  },
  "dateFormat": "YYYY-MMMM-DD", //change month format to MMMM from MM
  "dateTimeFormat": "YYYY-MM-DD HH:mm:ss",
  "weekFormat": "YYYY-wo",
  "monthFormat": "YYYY-MM"
};

export type RangeOptions = {
  name: string;
  getRange: () => [Dayjs, Dayjs] | null
}

export type Info = {
  range?: 'start' | 'end';
}

export type DateInputProps = {
  id?: string;
  name?: string;
  format?: string | string[];
  placeholder?: [string, string] | string;
  disabled?: boolean;
  variant?: 'outlined' | 'borderless' | 'filled';
  shouldDisableDates?: boolean;
  open?: boolean;
  mode?: PanelMode | [PanelMode, PanelMode];
  inputReadOnly?: boolean;
  handleOpenChange?: (open: boolean) => void;
  validationMessage?: string;
  allowEmpty?: [boolean, boolean];
  activeRangeOption?: RangeOptions;
  rangeOptions?: RangeOptions[];
  ref?: RefObject<HTMLElement>;
  max?: Dayjs;
  min?: Dayjs;
  value?: [Dayjs, Dayjs] | Dayjs;
  defaultPickerValue?: [Dayjs, Dayjs] | Dayjs;
  rangeDatePicker?: boolean;
  isRequired?: boolean;
  error?: string | null;
  setActiveRange?: (activeRange: RangeOptions) => void;
  onBackdropClick?: () => void;
  onClick?: () => void;
  onApply?: () => void;
  cancel?: () => void;
  apply?: () => void;
  handleChange?: (dates: [Dayjs | null, Dayjs | null] | Dayjs | null, dateStrings: [string, string] | string | null) => void;
  onCalendarChange?: (dates: [Dayjs | null, Dayjs | null], dateStrings: [string, string], info: Info) => void;
  onPanelChange?: (value: Dayjs | [Dayjs | null, Dayjs | null] | null, mode: PanelMode | [PanelMode, PanelMode]) => void;
  disabledDate?: (currentDate: Dayjs) => boolean;
}

export const RangePickerWrapper = observer(({
  id,
  name,
  placeholder,
  value,
  open,
  variant,
  handleOpenChange,
  format,
  inputReadOnly,
  allowEmpty,
  defaultPickerValue,
  validationMessage,
  rangeOptions,
  disabled,
  activeRangeOption,
  mode,
  onPanelChange,
  onBackdropClick,
  setActiveRange,
  onCalendarChange,
  handleChange,
  cancel,
  apply,
  onApply,
  disabledDate }: DateInputProps) => {
  const [wrapperRef, setWrapperRef] = useState<HTMLElement>();
  const [inputRef, setInputRef] = useState<HTMLElement>();
  const rangeRef = useRef(null);

  useOutsideMouseDown([wrapperRef as HTMLElement, inputRef as HTMLElement],
    () => {
      if (onBackdropClick) {
        onBackdropClick()
      }
    });

  useEffect(() => {
    if (open) {
      setInputRef(document.getElementsByClassName('ant-picker-range')[0] as HTMLElement);
      setWrapperRef(document.getElementsByClassName('ant-picker-dropdown')[0] as HTMLElement);
    } else {
      (rangeRef.current as HTMLElement | null)?.blur()
      setWrapperRef(undefined);
      setInputRef(undefined);
    }
  }, [open]);

  const applyChanges = (dates: [Dayjs | null, Dayjs | null] | null, dateStrings: [string, string]) => {
    if (handleChange) handleChange(dates, dateStrings);
    if (dates === null && onApply) { onApply() }
  }

  return (
    <RangePicker
      id={id}
      ref={rangeRef}
      name={name}
      format={format}
      variant={variant}
      onChange={applyChanges}
      placeholder={placeholder as [string, string]}
      mode={mode as [PanelMode, PanelMode] | undefined}
      value={value as [Dayjs, Dayjs]}
      disabled={disabled}
      inputReadOnly={inputReadOnly}
      open={open}
      locale={locale}
      allowEmpty={allowEmpty}
      onOpenChange={handleOpenChange}
      onPanelChange={onPanelChange as ((values: [Dayjs | null, Dayjs | null], modes: [PanelMode, PanelMode]) => void) | undefined}
      disabledDate={disabledDate}
      onCalendarChange={onCalendarChange}
      defaultPickerValue={defaultPickerValue as [Dayjs, Dayjs]}
      suffixIcon={<CalendarSVGIcon />}
      renderExtraFooter={() => (
        <DateInputFooter
          isRangePicker
          value={value as [Dayjs, Dayjs]}
          activeRangeOption={activeRangeOption}
          options={rangeOptions}
          validationMessage={validationMessage}
          setActiveRange={setActiveRange}
          handleChange={handleChange}
          onApply={onApply}
          cancel={cancel}
          apply={apply} />
      )}
    />
  );
})

export const DatePickerWrapper = observer(({
  id,
  name,
  placeholder,
  value,
  open,
  variant,
  handleOpenChange,
  format,
  inputReadOnly,
  defaultPickerValue,
  validationMessage,
  disabled,
  mode,
  error,
  onPanelChange,
  onBackdropClick,
  handleChange,
  cancel,
  apply,
  onApply,
  disabledDate }: DateInputProps) => {
  const [wrapperRef, setWrapperRef] = useState<HTMLElement>();
  const [inputRef, setInputRef] = useState<HTMLElement>();
  const rangeRef = useRef(null);

  useOutsideMouseDown([wrapperRef as HTMLElement, inputRef as HTMLElement],
    () => {
      if (onBackdropClick) {
        onBackdropClick()
      }
    });

  useEffect(() => {
    if (open) {
      setInputRef(document.getElementsByClassName('ant-picker-panel-container')[0] as HTMLElement);
      setWrapperRef(document.getElementsByClassName('ant-picker-dropdown')[0] as HTMLElement);
    } else {
      (rangeRef.current as HTMLElement | null)?.blur()
      setWrapperRef(undefined);
      setInputRef(undefined);
    }
  }, [open]);

  const applyChanges = (date: Dayjs | null, dateStrings: string | null) => {
    if (handleChange) handleChange(date, dateStrings);
    if (date === null && onApply) { onApply() }
  }

  return (
    <>
      <DatePicker
        className={`${error ? 'dateinput-error' : ''}`}
        id={id}
        ref={rangeRef}
        name={name}
        format={format}
        variant={variant}
        onChange={applyChanges}
        placeholder={placeholder as string}
        mode={mode as PanelMode | undefined}
        value={value as Dayjs ?? defaultPickerValue}
        disabled={disabled}
        inputReadOnly={inputReadOnly}
        open={open}
        locale={locale}
        onOpenChange={handleOpenChange}
        onPanelChange={onPanelChange as ((value: Dayjs | null, mode: PanelMode) => void) | undefined}
        disabledDate={disabledDate}
        suffixIcon={<CalendarSVGIcon />}
        renderExtraFooter={() => (
          <DateInputFooter
            value={value as Dayjs ?? defaultPickerValue}
            validationMessage={validationMessage}
            isRangePicker={false}
            handleChange={handleChange}
            onApply={onApply}
            cancel={cancel}
            apply={apply} />
        )} />

      {error &&
        <Feedback
          className="date-input-feedback"
          status='error'
          message={error} />}
    </>

  );
})

type ControlledProps = DateInputProps & {
  model: DateInputState;
  onApply?: () => void;
};

export const DateInput = observer(
  ({ model, onApply }: ControlledProps) => {

    if (model.isRangeDatepicker)
      return (
        <RangePickerWrapper
          activeRangeOption={model.activeRangeOption || undefined}
          defaultPickerValue={model.defaultPickerValue || undefined}
          id={model.id}
          value={model.value || undefined}
          open={model.open}
          mode={model.mode as PanelMode | [PanelMode, PanelMode] | undefined}
          variant={model.variant}
          format={model.format}
          disabled={model.disabled}
          inputReadOnly={model.inputReadOnly}
          allowEmpty={model.allowEmpty}
          error={model.error}
          rangeOptions={model.rangeOptions}
          validationMessage={model.validationMessage || undefined}
          onPanelChange={model.onPanelChange}
          onBackdropClick={model.onBackdropClick}
          onCalendarChange={model.onCalendarChange}
          setActiveRange={model.setActiveRange}
          handleChange={model.handleChange}
          handleOpenChange={model.handleOpenChange}
          disabledDate={model.disabledDate}
          cancel={model.cancel}
          apply={model.apply}
          onApply={onApply}
          placeholder={model.placeholder || undefined}
        ></RangePickerWrapper>
      )

    return (
      <DatePickerWrapper
        defaultPickerValue={model.defaultPickerValue as Dayjs}
        id={model.id}
        value={model.value || undefined}
        open={model.open}
        mode={model.mode as PanelMode | [PanelMode, PanelMode] | undefined}
        variant={model.variant}
        format={model.format}
        disabled={model.disabled}
        inputReadOnly={model.inputReadOnly}
        validationMessage={model.validationMessage || undefined}
        onPanelChange={model.onPanelChange}
        onBackdropClick={model.onBackdropClick}
        onCalendarChange={model.onCalendarChange}
        setActiveRange={model.setActiveRange}
        handleChange={model.handleChange}
        handleOpenChange={model.handleOpenChange}
        disabledDate={model.disabledDate}
        error={model.error}
        cancel={model.cancel}
        apply={model.apply}
        onApply={onApply}
        placeholder={model.placeholder as [string, string] || undefined} />)
  })
